import { useMemo } from 'react';

import { useGetExternalOffersQuery } from '@ocx/graphql';

import { createExternalOffersQueryVariables } from '../external-loyalty/external-offers.utils';
import { useDisplayCategories, type UseDisplayCategoriesFilter } from '../use-display-categories.hook';
import { groupOfferAndCategories } from './group-offers-display-categories.utils';

import type { Offer } from '../offer.types';

export type UseOffersCarouselCategoriesOptions = {
  persistToStorage?: boolean;
};

export const useOffersCarouselCategories = (
  offers: Offer[],
  filter: UseDisplayCategoriesFilter,
  options: UseOffersCarouselCategoriesOptions = {},
) => {
  const { persistToStorage } = options;
  const {
    data: categories,
    loading: categoriesLoading,
    refetch: refetchCategories,
  } = useDisplayCategories({
    filter,
    persistToStorage,
  });

  const variables = useMemo(() => createExternalOffersQueryVariables(categories), [categories]);
  const skipExternal = !variables?.filter.bannerConfigurationUuidIncludes.length;
  const { data: externalBanners, loading: externalLoading } = useGetExternalOffersQuery({
    skip: skipExternal,
    variables,
  });

  const grouped = useMemo(
    () => groupOfferAndCategories(categories, offers, externalBanners?.banners),
    [offers, categories, externalBanners?.banners],
  );

  const loading = categoriesLoading || (!skipExternal && externalLoading);

  return { categories: grouped, loading, refetchCategories };
};
